import { useEffect, useState } from "react";
import { Application } from "./trackConfig";
import TrackRoute, { TrackRouteProps } from "./TrackRoute";
import { matchPath, useLocation } from "react-router-dom";
import config from "./trackConfig";
import { getLauncherTrackingContext, COHERE_LAUNCHER_ORIGIN } from "./launcherContext";

export default function TrackUsage({ baseUrl, application }: { baseUrl: string; application: Application }) {
  const location = useLocation();
  const path = location.pathname;
  const search = location.search;
  const [trackProps, setProps] = useState<TrackRouteProps>();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const caseIdParams = searchParams.get("caseId");
    // RW-747: when the launcher origin is active, review-route trackers are emitted as
    // COHERE_LAUNCHER (case-agnostic) instead of AUTH. Tenancy rides on the launcher context.
    const launcherContext = getLauncherTrackingContext();
    const launcherActive = launcherContext?.origin === COHERE_LAUNCHER_ORIGIN;
    for (const [trackPath, currentConfig] of Object.entries(config)) {
      if (currentConfig.active && currentConfig.application === application) {
        // launcherOnly routes (note/letter) only track when the launcher origin is active.
        if (currentConfig.launcherOnly && !launcherActive) {
          continue;
        }
        const pattern = matchPath({ path: trackPath }, path);
        if (pattern) {
          // Launcher tracking only applies to WORKING routes; NON_WORKING components
          // (Dashboard/Case Complete) are never emitted under COHERE_LAUNCHER.
          const isLauncher = launcherActive && currentConfig.category === "WORKING";
          const queryParamNames = Array.from(searchParams.keys());
          const requiredParamsMet = currentConfig.requiredParams?.every((elem) => queryParamNames.includes(elem));
          // Launcher trackers are case-agnostic and start before reviewId/caseId exist (attached
          // later, RW-746), so the caseId/reviewId requiredParams gate does not apply under launcher.
          if (isLauncher || !currentConfig.requiredParams || requiredParamsMet) {
            const caseIdPath = pattern.params.caseId;
            const queueIdParams = searchParams.get("queueId") || undefined;
            const reviewIdParams = searchParams.get("reviewId") || undefined;
            setProps({
              baseUrl,
              application: isLauncher ? "COHERE_LAUNCHER" : application,
              // Under launcher origin, use the specific action label the reviewer picked (e.g. "MD
              // clinical review") so reviews aren't all collapsed to the route's "Clinical Review";
              // fall back to the route config's component if the context didn't carry one.
              component: (isLauncher && launcherContext?.component) || currentConfig.component,
              category: currentConfig.category,
              serviceRequestId: pattern.params.serviceRequestId,
              // Launcher trackers are case-agnostic: omit caseId/queueId.
              caseId: isLauncher ? undefined : caseIdPath || caseIdParams,
              queueId: isLauncher ? undefined : queueIdParams,
              reviewId: reviewIdParams,
              source: isLauncher ? "COHERE_LAUNCHER" : undefined,
              healthPlanName: isLauncher ? launcherContext?.healthPlanName : undefined,
              delegatedVendorName: isLauncher ? launcherContext?.delegatedVendorName : undefined,
              launcherRequiresReviewId: isLauncher ? currentConfig.launcherRequiresReviewId : undefined,
              // RW-747: the enableTimeTracker decision was evaluated on the landing page (inside the
              // providers) and stored on the launcher context. TrackRoute renders outside those
              // providers, so it reads the stored decision here instead of evaluating the flag.
              trackingEnabled: isLauncher ? launcherContext?.trackingEnabled ?? false : undefined,
            });
            return;
          }
        }
      }
    }
    setProps(undefined);
  }, [application, baseUrl, path, search]);

  return trackProps ? <TrackRoute {...trackProps} /> : null;
}
